import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useHMSStore,
  selectLocalPeer,
  useHMSActions,
  selectIsConnectedToRoom
} from "@100mslive/react-sdk";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";

import RemoteLayout from "./layout";
import Peer from "../../components/videoCalling/peer";
import { WorkshopContext } from "../../contexts/workshop.context";
import { UserIcon, CallControl } from "../../components/videoCalling";
import { defaultRoles } from "../../util/workshop.util";

const pageRoleName = "getting-ready";
const PreReady = () => {
  const { t } = useTranslation();
  const { workshopDetails, user, socket } = useContext(WorkshopContext);
  const localPeer = useHMSStore(selectLocalPeer);
  const isConnected = useHMSStore(selectIsConnectedToRoom);
  const hmsActions = useHMSActions();
  const navigate = useNavigate();
  const [previewStarted, setPreviewStarted] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);

  // Start preview only after user interaction (to satisfy browser autoplay policies)
  const handleStartPreview = async () => {
    // If preview is already active (localPeer exists), just mark user as interacted
    // This handles the case where App.js auto-started preview before user interaction
    if (localPeer && !userInteracted) {
      setUserInteracted(true);
      setPreviewStarted(true);
      return;
    }

    // If already interacted or preview started, don't proceed
    if (
      !workshopDetails?.callJoiningCode?.code ||
      !user?.name ||
      isConnected ||
      previewStarted ||
      userInteracted
    ) {
      return;
    }

    try {
      setUserInteracted(true);
      const authToken = await hmsActions.getAuthTokenByRoomCode({
        roomCode: workshopDetails?.callJoiningCode?.code
      });
      await hmsActions.preview({ userName: user?.name, authToken });
      setPreviewStarted(true);
    } catch (error) {
      console.error("Error starting preview in PreReady:", error);
      setUserInteracted(false); // Reset on error so user can try again
    }
  };

  const handleNext = async () => {
    const authToken = await hmsActions.getAuthTokenByRoomCode({
      roomCode: workshopDetails?.callJoiningCode?.code
    });
    Cookies.set("setupCompleted", true);
    await hmsActions.join({ userName: user?.name, authToken });

    socket.emit("syncToWorkshop", "", (url) => {
      console.log("syncToWorkshop==>", workshopDetails, url);
      const redirectUrl = workshopDetails.workshopEnded
        ? "/online/pre-summary"
        : url || "/online/wait";
      navigate(redirectUrl);
    });
  };

  useEffect(() => {
    if (!localPeer || localPeer?.roleName === pageRoleName || !hmsActions || !localPeer.id) return;
    const timeout = setTimeout(() => {
      handleChangeRole();
    }, 1000);

    return () => clearTimeout(timeout);
  }, [localPeer, hmsActions]);

  const handleChangeRole = async () => {
    // await hmsActions.changeRoleOfPeer(localPeer.id, pageRoleName, true);
  };

  // Show initial screen with user icon, disabled controls, and test camera button
  if (!userInteracted) {
    return (
      <RemoteLayout hideCallControl={true}>
        <div className="flex flex-col items-center justify-center text-white min-h-[60vh]">
          <div className="mb-6 text-center">
            <h1 className="text-4xl font-semibold text-white-100 mb-4">
              {t("preReady.preInteraction.welcome")}
            </h1>
            <p className="mt-2 text-white-100 max-w-xl mx-auto">
              {t("preReady.preInteraction.description")}
            </p>
          </div>

          <div className="flex flex-col items-center gap-8 w-full max-w-4xl">
            {/* Video Preview Area */}
            <div className="relative bg-black rounded-lg overflow-hidden">
              <UserIcon
                name={user?.name}
                src={user?.avatarUrl}
                size="2xl"
                variant={user?.role === defaultRoles.participant ? "default" : "moderator"}
              />
            </div>

            {/* Video Controls - Disabled */}
            <div className="w-full max-w-md opacity-50 pointer-events-none">
              <CallControl />
            </div>

            {/* Test Camera Button */}
            <button
              onClick={handleStartPreview}
              className={`px-12 py-4 rounded-full text-xl font-bold text-white-100 shadow-lg transition-all duration-200 hover:opacity-90 ${
                user?.role === defaultRoles.participant
                  ? "bg-orange-default hover:bg-orange-500"
                  : "bg-green-500 hover:bg-green-600"
              }`}>
              {t("preReady.preInteraction.testCamera") || "Test Camera"} →
            </button>
          </div>
        </div>
      </RemoteLayout>
    );
  }

  // Show preview screen (camera & mic check) with footer controls and Next button
  return (
    <RemoteLayout
      buttonText={t("preReady.joinButton") + " →"}
      buttonColor={user?.role === defaultRoles.participant ? "orange" : "green"}
      onButtonClick={localPeer && handleNext}>
      <div className="flex flex-col items-center justify-center text-white">
        <div className="mb-6 text-center">
          <h1 className="text-4xl font-semibold text-white-100">{t("preReady.welcome")}</h1>
          <p className="mt-2 text-white-100">{t("preReady.instructions")}</p>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-center gap-20 w-full max-w-4xl">
          <div className="relative  bg-black rounded-lg overflow-hidden">
            {localPeer ? (
              <Peer size="2xl" peer={localPeer} />
            ) : (
              <UserIcon
                name={user?.name}
                src={user?.avatarUrl}
                size="2xl"
                variant={user?.role === defaultRoles.participant ? "default" : "moderator"}
              />
            )}
          </div>

          {/* Right Side Content */}
          <div className="bg-orange-700 shadow-lg rounded-2xl p-10 w-full max-w-md">
            <div className="mb-4">
              <h2 className="text-center text-xl font-bold text-orange-default mb-4">
                {t("preReady.readyToJoin")}
              </h2>
              <p>{t("preReady.description")}</p>
            </div>
          </div>
        </div>
      </div>
    </RemoteLayout>
  );
};

export default PreReady;
